import Icon from "assets/icons";
import React, { Suspense } from "react";
import Button from "shared/components/button";
import Variant from "shared/enums/Variant";
import { useCopies } from "shared/hooks/useCopies";
import { downloadFileFromUrl } from "shared/utils";
import lazyLoad from "shared/utils/lazyLoad";
const QRCodeBase = lazyLoad(() => import("./index"));

export function DisplayQRCode({
  downloadSuccessHandler,
  ...props
}: {
  value: string;
  size: number;
  label: string;
  downloadSuccessHandler?: () => void;
}) {
  const copies = useCopies();
  const qrRef = React.useRef<HTMLDivElement>(null);
  const handleQRCodeDownload = React.useCallback(() => {
    if (qrRef.current) {
      const svg = qrRef.current.querySelector("svg");
      if (!svg) {
        return;
      }
      if (!svg.hasAttribute("xmlns")) {
        svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }
      let svgData = new XMLSerializer().serializeToString(svg);

      // Assuming the QR code itself is smaller than 300x300 and needs to be centered
      const qrSize = props.size; // The actual size of the QR code
      const svgSize = 300; // The desired size of the final SVG
      const yOffset = (svgSize - qrSize) / 2; // Calculate the vertical offset

      // Adjust the height of the SVG to include space for the text
      const adjustedSvgHeight = svgSize + 50; // Add 50px of space for the text

      // Sample text to add below the QR code
      const sampleText = props.label;
      const textYPosition = svgSize + 20; // Position the text 20px below the QR code
      let svgTextElement = "";
      if (sampleText.length > 100) {
        svgTextElement = `<text x="50%" y="${textYPosition}" font-family="Arial" font-size="24" text-anchor="middle" font-weight="bold" style="font-weight:bold; text-transform:capitalize" >${sampleText.slice(
          0,
          100
        )}</text>
            <text x="50%" y="${
              textYPosition + 30
            }" font-family="Arial" font-size="24" text-anchor="middle" font-weight="bold" style="font-weight:bold; text-transform:capitalize" >-${sampleText.slice(
          100
        )}</text>`;
      } else {
        svgTextElement = `<text x="50%" y="${textYPosition}" font-family="Arial" font-size="24" text-anchor="middle" font-weight="bold" style="font-weight:bold; text-transform:capitalize" >${sampleText}</text>`;
      }

      svgData = `<svg width="${svgSize}" height="${adjustedSvgHeight}" viewBox="0 0 ${svgSize} ${adjustedSvgHeight}" xmlns="http://www.w3.org/2000/svg" style="display: block; margin: auto; background: white; width:100%;">
          <foreignObject width="${qrSize}" height="${qrSize}" x="0" y="${yOffset}" style="width:100%; text-align:center">
             ${svgData}
          </foreignObject>
          ${svgTextElement}
        </svg>`;

      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);
      downloadFileFromUrl(svgUrl, props.label);
      downloadSuccessHandler?.();
    }
  }, [props.label, props.size, downloadSuccessHandler]);
  return (
    <Suspense
      fallback={
        <div
          className="displayQrCodeFallback"
          style={{
            height: props.size,
            width: props.size
          }}>
          {copies.get("loading")}...
        </div>
      }>
      <div className="displayQrCodeContent">
        <QRCodeBase value={props.value} size={props.size} qrRef={qrRef} />
        <Button
          isOutline
          variant={Variant.LINK}
          onClick={handleQRCodeDownload}
          icon={<Icon.DownloadImage size={15} />}
          label={copies.get("contact.context.btn.qr.download")}
        />
      </div>
    </Suspense>
  );
}
